export type CmtsFilterQuery = Record<string, string | number | boolean | undefined | null>;
export type CmtsRow = Record<string, unknown>;

export function normalizeCmtsFilterValue(value: unknown): string {
  return String(value ?? "").trim().toLowerCase().replace(/[_-]+/g, " ");
}

function text(row: CmtsRow, keys: readonly string[]): string {
  return keys.map((key) => String(row[key] ?? "")).join(" ");
}

function isPastDue(row: CmtsRow): boolean {
  const raw = row.dueDate ?? row.dueAt ?? row.targetDate ?? row.slaDueDate;
  if (!raw) return false;
  const time = new Date(String(raw)).getTime();
  if (!Number.isFinite(time)) return false;
  return time < Date.now();
}

function rowDateTime(row: CmtsRow): number | null {
  const raw = row.createdAt ?? row.updatedAt ?? row.reportedDate ?? row.occurredAt ?? row.dueDate ?? row.dueAt ?? row.slaDueDate;
  if (!raw) return null;
  const time = new Date(String(raw)).getTime();
  return Number.isFinite(time) ? time : null;
}

function isTruthyLike(value: string): boolean {
  return ["true", "yes", "1", "y"].includes(value);
}

const FIELD_ALIASES: Record<string, readonly string[]> = {
  q: ["complaintNumber", "title", "description", "status", "workflowStage", "category", "source", "sourceChannel", "priority", "slaStatus"],
  search: ["complaintNumber", "title", "description", "status", "workflowStage", "category", "source", "sourceChannel", "priority", "slaStatus"],
  searchText: ["complaintNumber", "title", "description", "status", "workflowStage", "category", "source", "sourceChannel", "priority", "slaStatus"],
  status: ["status", "statusCode", "statusLabel", "result", "workflowStage"],
  statusGroup: ["status", "statusCode", "statusLabel", "result", "workflowStage"],
  statusLabel: ["status", "statusCode", "statusLabel", "result", "workflowStage"],
  slaStatus: ["slaStatus", "status"],
  priority: ["priority"],
  severity: ["severity"],
  sourceType: ["source", "sourceType", "sourceChannel", "complaintType"],
  repeat: ["repeatComplaint", "repeat", "recurrenceCount"],
  reopened: ["reopened", "status", "statusLabel"],
  closureBlocked: ["closureBlockStatus", "closureBlocked", "closureBlocks", "status"],
  containmentStatus: ["containmentStatus", "status", "workflowStage"],
  sourceChannel: ["source", "sourceType", "sourceChannel", "complaintType"],
  workflowStage: ["workflowStage", "status", "statusLabel"],
  category: ["category", "categoryId", "subcategory"],
  assignee: ["ownerId", "ownerName", "assignedToUserId", "verifierUserId"],
  owner: ["ownerId", "ownerName", "assignedToUserId", "verifierUserId"],
  confidentiality: ["confidentiality", "visibility", "classification"],
  type: ["type", "actionType", "eventType", "complaintType", "source"],
  eventType: ["eventType", "type"],
  due: ["dueDate", "dueAt", "targetDate", "slaDueDate"],
};

export function cmtsRecordMatchesQuery(row: CmtsRow, query: CmtsFilterQuery): boolean {
  return Object.entries(query).every(([key, raw]) => {
    if (raw === undefined || raw === null || raw === "" || raw === "all") return true;
    if (["tenantId", "pageNumber", "pageSize", "sortBy", "sortDirection", "sessionKey", "reportCode", "exportFormat", "format", "requestedAt", "scheduledRun", "download", "row", "rows", "visibleColumns"].includes(key)) return true;
    if (["dateFromUtc", "dateFrom", "fromDate"].includes(key)) {
      const actualTime = rowDateTime(row);
      const expectedTime = new Date(String(raw)).getTime();
      return actualTime === null || !Number.isFinite(expectedTime) ? true : actualTime >= expectedTime;
    }
    if (["dateToUtc", "dateTo", "toDate"].includes(key)) {
      const actualTime = rowDateTime(row);
      const expectedTime = new Date(String(raw)).getTime();
      return actualTime === null || !Number.isFinite(expectedTime) ? true : actualTime <= expectedTime;
    }
    const expected = normalizeCmtsFilterValue(raw);
    if (!expected) return true;
    const keys = FIELD_ALIASES[key] ?? [key];
    const actual = normalizeCmtsFilterValue(text(row, keys));
    if (!actual) return false;
    if (key === "statusGroup" && expected === "open") return !["closed", "cancelled", "rejected", "approved closure"].some((closed) => actual.includes(closed));
    if (key === "repeat") return isTruthyLike(expected) ? isTruthyLike(normalizeCmtsFilterValue(row.repeatComplaint ?? row.repeat)) || Number(row.recurrenceCount ?? 0) > 0 : true;
    if (key === "closureBlocked") return isTruthyLike(expected) ? isTruthyLike(normalizeCmtsFilterValue(row.closureBlocked)) || actual.includes("block") || actual.includes("missing") || actual.includes("open") : true;
    if (expected === "open") return !["closed", "cancelled", "rejected"].some((closed) => actual.includes(closed));
    if (expected === "pending") return actual.includes("pending") || actual.includes("required") || actual.includes("draft") || actual.includes("submitted") || actual.includes("in progress") || actual.includes("review");
    if (expected === "overdue") return actual.includes("overdue") || actual.includes("breach") || isPastDue(row);
    return actual.includes(expected) || expected.includes(actual);
  });
}

export function applyCmtsQueryFilters<T extends CmtsRow>(rows: readonly T[], query: CmtsFilterQuery): T[] {
  return rows.filter((row) => cmtsRecordMatchesQuery(row, query));
}

export function queryObjectFromSearch(search: string): CmtsFilterQuery {
  const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`);
  const query: CmtsFilterQuery = {};
  params.forEach((value, key) => { query[key] = value; });
  return query;
}
